<script setup lang="ts">
import { safeAreaTopOffset } from '@/services/safeAreaService'
import { Z_INDEX } from '@/utils/zIndex'

defineOptions({ name: 'MapCollectionTitle' })

// Floating title at top-center of the map (mobile detail levels) — the centre of the back / title /
// sync nav row, mirroring the desktop "back + title" pattern. Takes the wordmark's position when a
// collection is open. Non-interactive context label; actions live in the sheet (like the desktop
// card), not here. Visibility is owned by the parent (v-if).
//
// `parent` or `byline` turns it into a two-line stack (Instagram-style nav title): the current page
// name is the prominent top line and context sits smaller and muted underneath. `dotColor` adds
// the subject's accent dot before the title (used for a track) so the colour identity lives here —
// the sheet no longer needs to repeat the name + dot, freeing its vertical space for the map.
defineProps<{
  title: string
  titleByline?: string | null
  parent?: string | null
  byline?: string | null
  dotColor?: string | null
}>()

// Vertically centred on the sync button's row (safe-area-top + 34px), like the brand wordmark.
const topOffset = safeAreaTopOffset(34)
</script>

<template>
  <div
    class="fixed left-1/2 max-w-[calc(100vw-8.5rem)] -translate-x-1/2 -translate-y-1/2 border border-black/5 bg-white/90 px-4 py-1 text-center shadow-sm backdrop-blur"
    :class="parent || byline ? 'rounded-2xl' : 'rounded-full'"
    :style="{ top: topOffset, zIndex: Z_INDEX.MAP_WORDMARK }"
    data-testid="map-collection-title"
  >
    <div class="flex items-center justify-center gap-1.5">
      <span
        v-if="dotColor"
        class="h-2 w-2 shrink-0 rounded-full"
        :style="{ backgroundColor: dotColor }"
      ></span>
      <span class="min-w-0 truncate text-sm font-semibold leading-tight text-neutral-800">
        {{ title }}
      </span>
      <span
        v-if="titleByline"
        class="shrink-0 text-[11px] font-medium leading-tight text-neutral-500"
        data-testid="map-title-creator-byline"
      >
        {{ titleByline }}
      </span>
    </div>
    <div v-if="parent" class="truncate text-[11px] font-medium leading-tight text-neutral-500">
      {{ parent }}
    </div>
    <div v-else-if="byline" class="truncate text-[11px] font-medium leading-tight text-neutral-500">
      by {{ byline }}
    </div>
  </div>
</template>
